import { StatusChip } from '@/components/ui/status-chip';
import { useGetChildConversations } from '@/hooks/useChat';
import type { Conversation } from '@/types';
import { cn } from '@/utils/classname';
import { formatDate } from '@/utils/date.util';

import { SidebarListItem } from './SidebarListItem';
import { sortByCreatedDesc } from './sortByCreatedDesc';

interface ConversationNodeProps {
  conversation: Conversation;
  depth: number;
  selected: string | null;
  onSelect: (id: string) => void;
  runningId?: string;
}

/**
 * One row in the Chat-pane sidebar, plus its subagent children rendered
 * recursively beneath it. Subagents are conversations whose `parent_id`
 * points at this conversation's UUID — fetched via the same hook the
 * legacy AIAssistant uses.
 *
 * Each level subscribes its own poller, so a deep tree fans out queries.
 * In practice hunt iterations spawn a handful of subagents at most; if
 * we ever see deeper / wider trees we can fetch-on-expand instead.
 */
export function ConversationNode({
  conversation,
  depth,
  selected,
  onSelect,
  runningId,
}: ConversationNodeProps) {
  const { data: rawChildren = [] } = useGetChildConversations(
    conversation.uuid
  );
  const children = sortByCreatedDesc(rawChildren);
  const isRunning = conversation.uuid === runningId;
  const isSubagent = depth > 0;
  // Subagent convs are titled "Subagent: <agentName>" by spawn_agent_sync;
  // strip the prefix for display, falling back to the title or short UUID.
  const label = isSubagent
    ? (conversation.title?.replace(/^Subagent:\s*/, '') ??
      conversation.uuid.slice(0, 8))
    : conversation.uuid.slice(0, 8);
  const indent = depth * 12;

  return (
    <>
      <SidebarListItem
        isSelected={conversation.uuid === selected}
        onClick={() => onSelect(conversation.uuid)}
      >
        <div
          className="flex items-center justify-between gap-1"
          style={{ paddingLeft: indent }}
        >
          <span
            className={cn(
              'truncate',
              isSubagent ? 'text-chariot-text-secondary' : 'font-mono'
            )}
          >
            {isSubagent ? `↳ ${label}` : label}
          </span>
          {isRunning ? <StatusChip label="Running" variant="info" /> : null}
        </div>
        <div
          className="text-chariot-text-secondary mt-1 text-xs"
          style={{ paddingLeft: indent }}
        >
          {formatDate(conversation.created)}
        </div>
      </SidebarListItem>
      {children.map(child => (
        <ConversationNode
          key={child.uuid}
          conversation={child}
          depth={depth + 1}
          selected={selected}
          onSelect={onSelect}
          runningId={runningId}
        />
      ))}
    </>
  );
}
